import axios from 'axios';

import { RECORDING_ARTIFACTS, RECORDING_INFORMATION } from '../../config/apiPaths';
import {
    type ClassSessionInfo,
    type ClassStudent,
    type ClassStudentGroup,
    type GroupSectionLabel
} from '../../context/types';
import { type ArtifactType, type ResponseRecordingInformation } from '../../types/recordingTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { processApiPath } from '../../utils/url';

/**
 * Get groups attributed to a session.
 */
export function getGroupsForSession(
    session: ClassSessionInfo,
    classGroups: ClassStudentGroup[],
    myGroups: ClassStudentGroup[],
    role?: string
): ClassStudentGroup[] {
    if (!session.class_session_group) {
        return [];
    }
    const { selectedGroups: sessionAssignedGroupIds = [] } = session.class_session_group.config;

    const privilegedUser = role === 'teacher' || role === 'assistant';
    const groups: ClassStudentGroup[] = [];

    sessionAssignedGroupIds.forEach((sessionAssignedGroupId) => {
        const groupsToSearch = privilegedUser ? classGroups : myGroups;
        const foundGroup = groupsToSearch.find(
            (group) => String(group.c_student_group_id) === String(sessionAssignedGroupId)
        );
        if (foundGroup) {
            groups.push(foundGroup);
        }
    });
    return groups;
}

/**
 *  Get the group labels for a session.
 */

export function getGroupLabelsForSession(
    session: ClassSessionInfo,
    classGroups: ClassStudentGroup[],
    myGroups: ClassStudentGroup[],
    showSections: boolean,
    role?: string
): GroupSectionLabel[] {
    if (!session.class_session_group) {
        return [];
    }
    const { label, selectedGroups: sessionAssignedGroupIds = [] } = session.class_session_group.config;

    if (!showSections) {
        return [{ label }];
    }

    if (sessionAssignedGroupIds.includes('all')) {
        return [{ label, section: 'all' }];
    }
    const groups = getGroupsForSession(session, classGroups, myGroups, role);
    return groups.map((group) => ({ label, section: group.label }));
}

/**
 * Filter sessions to only include sessions where the user is in an associated group.
 */
export function filterSessionsByGroupAccess(
    sessions: ClassSessionInfo[],
    classGroups: ClassStudentGroup[],
    myGroups: ClassStudentGroup[],
    me: Partial<ClassStudent>
) {
    return sessions.filter((session) => {
        if (!session?.class_session_group) {
            return false;
        }
        const { selectedGroups: sessionAssignedGroupIds = [] } = session.class_session_group.config;
        if (sessionAssignedGroupIds.includes('all')) {
            // This session is appropriate for inclusion as it applies to all groups.
            return true;
        }
        const groups = getGroupsForSession(session, classGroups, myGroups, me?.role);
        // If groups has length, I am in a group attributed to this session and the session can be included.
        return groups.length;
    });
}

export async function getRecordingArtifactURLs(
    schoolUuid: string,
    userUuid: string,
    classUuid: string,
    recordingUuid: string
): Promise<Record<string, string>> {
    const headers = { 'Content-Type': 'application/json', ...getUserUuidHeaders(userUuid) };

    const contentsList = [
        'active_speaker',
        'audio_only',
        'audio_only_each_participant',
        'audio_transcript',
        'closed_caption',
        'composite',
        'gallery_view',
        'host_video',
        'shared_screen',
        'shared_screen_with_gallery_view',
        'shared_screen_with_speaker_view',
        'shared_screen_with_speaker_view(CC)',
        'timeline'
    ];
    const { data } = await axios.get(
        processApiPath(
            RECORDING_ARTIFACTS,
            {
                schoolUuid,
                classUuid,
                recordingUuid
            },
            { contents_list: contentsList.join(',') }
        ),
        { headers }
    );
    const urlsByContentType = {};
    // eslint-disable-next-line no-restricted-syntax
    for (const artifact of data.artifacts) {
        if (!artifact.error && artifact.url) {
            urlsByContentType[artifact.contents] = artifact.url;
        }
    }
    return urlsByContentType;
}

export async function getRecordingArtifact(
    schoolUuid: string,
    userUuid: string,
    classUuid: string,
    recordingUuid: string,
    artifactType: ArtifactType
) {
    const headers = { 'Content-Type': 'application/json', ...getUserUuidHeaders(userUuid) };
    const { data } = await axios.get(
        processApiPath(
            RECORDING_ARTIFACTS,
            {
                schoolUuid,
                classUuid,
                recordingUuid
            },
            { contents_list: artifactType }
        ),
        { headers }
    );
    return data.artifacts[0];
}

export async function getRecordingInfo(
    schoolUuid: string,
    userUuid: string,
    classUuid: string,
    recordingUuid: string
): Promise<ResponseRecordingInformation | null> {
    const headers = { 'Content-Type': 'application/json', ...getUserUuidHeaders(userUuid) };
    try {
        const { data: recordingInfo } = await axios.get<ResponseRecordingInformation>(
            processApiPath(RECORDING_INFORMATION, {
                schoolUuid,
                classUuid,
                recordingUuid
            }),
            { headers }
        );

        return recordingInfo;
    } catch (e) {
        console.error(e);
    }
    return null;
}
